import { View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import * as Burnt from "burnt"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { configSchema } from "@white-labels/input"

import { Text, TextInput, Button, Checkbox, Header, KeyboardAwareScrollView, ActivityIndicator } from "@src/components"
import { ar } from "@src/i18n/ar"
import { trpc } from "@src/utils"

export function ConfigForm() {
  const utils = trpc.useUtils()
  const configQuery = trpc.config.get.useQuery()

  const { control, handleSubmit } = useForm({ resolver: zodResolver(configSchema), defaultValues: configQuery.data ?? undefined })

  const updateMutation = trpc.config.update.useMutation({
    onSuccess: () => {
      utils.config.get.invalidate()
      Burnt.toast({ title: ar.saved, preset: "done" })
    },
    onError: (e) => Burnt.toast({ title: e.message, preset: "error" }),
  })

  const onSubmit = handleSubmit((data) => {
    updateMutation.mutate(data)
  })

  return (
    <KeyboardAwareScrollView contentContainerStyle={{ padding: 16, gap: 16, maxWidth: 800, width: "100%", alignSelf: "center" }}>
      {configQuery.data?.channel ? (
        <View className="self-start rounded-full bg-gray-100 px-3 py-1">
          <Text className="text-sm font-semibold uppercase text-gray-600">{configQuery.data.channel}</Text>
        </View>
      ) : null}

      <Controller control={control} name="maintenance" render={({ field: { onChange, value } }) => <Checkbox value={value} onChange={onChange} label={ar.maintenance} />} />

      <Controller control={control} name="busy" render={({ field: { onChange, value } }) => <Checkbox value={value} onChange={onChange} label={ar.busy} />} />

      <Controller
        control={control}
        name="message"
        render={({ field: { onChange, value } }) => (
          <View style={{ gap: 8 }}>
            <Text className="font-bold">{ar.configMessage}</Text>
            <TextInput value={value ?? ""} onChangeText={onChange} multiline placeholder={ar.configMessagePlaceholder} />
          </View>
        )}
      />

      <View style={{ gap: 8 }}>
        <Text className="font-bold">{ar.version}</Text>
        <View className="flex-row" style={{ gap: 8 }}>
          <Controller
            control={control}
            name="version.latest"
            render={({ field: { onChange, value } }) => (
              <View className="flex-1" style={{ gap: 4 }}>
                <Text>{ar.latestVersion}</Text>
                <TextInput value={value} onChangeText={onChange} />
              </View>
            )}
          />
          <Controller
            control={control}
            name="version.minimum"
            render={({ field: { onChange, value } }) => (
              <View className="flex-1" style={{ gap: 4 }}>
                <Text>{ar.minimumVersion}</Text>
                <TextInput value={value} onChangeText={onChange} />
              </View>
            )}
          />
        </View>
      </View>

      <View style={{ gap: 8 }}>
        <Text className="font-bold">{ar.storeLinks}</Text>
        <View style={{ gap: 8 }}>
          <Controller
            control={control}
            name="storeLink.android"
            render={({ field: { onChange, value } }) => (
              <View style={{ gap: 4 }}>
                <Text>Android</Text>
                <TextInput value={value} onChangeText={onChange} />
              </View>
            )}
          />
          <Controller
            control={control}
            name="storeLink.ios"
            render={({ field: { onChange, value } }) => (
              <View style={{ gap: 4 }}>
                <Text>iOS</Text>
                <TextInput value={value} onChangeText={onChange} />
              </View>
            )}
          />
        </View>
      </View>

      <Button loading={updateMutation.isPending} onPress={onSubmit} className="mt-4">
        {ar.save}
      </Button>
      <SafeAreaView edges={["bottom"]} />
    </KeyboardAwareScrollView>
  )
}

export default function ConfigEditor() {
  const configQuery = trpc.config.get.useQuery()

  if (configQuery.isLoading) {
    return (
      <>
        <Header title={ar.config} />
        <ActivityIndicator className="mx-auto mt-8" />
      </>
    )
  }

  return (
    <>
      <Header title={ar.config} />
      <ConfigForm />
    </>
  )
}
